import { Component, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { AppCommonModule } from 'src/app/common.module';
import { InitializeService } from 'src/app/services/initialize.service';
import { AppReferenceDataStore } from 'src/app/state/app-reference-data.store';
import { AppSessionStore } from 'src/app/state/app-session.store';

const BOOTSTRAP_ERROR_MESSAGE_KEYS = {
  authentication: 'bootstrap.errors.authentication',
  network: 'bootstrap.errors.network',
  permission: 'bootstrap.errors.permission',
  missing: 'bootstrap.errors.missing',
  unknown: 'bootstrap.errors.unknown',
};

@Component({
  selector: 'app-bootstrap-loading-overlay',
  templateUrl: './bootstrap-loading-overlay.component.html',
  styleUrls: ['./bootstrap-loading-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AppCommonModule],
})
export class BootstrapLoadingOverlayComponent {
  private sessionStore = inject(AppSessionStore);
  private referenceDataStore = inject(AppReferenceDataStore);
  private initializeService = inject(InitializeService);

  readonly bootstrapStatus = this.sessionStore.bootstrapStatus;
  readonly authenticationStatus = this.sessionStore.authenticationStatus;
  readonly isPendingSiteActivation = this.sessionStore.isPendingSiteActivation;
  readonly referenceDataStatus = computed(() => ({
    usersStatus: this.referenceDataStore.usersStatus(),
    clientsStatus: this.referenceDataStore.clientsStatus(),
    sitesStatus: this.referenceDataStore.sitesStatus(),
    optionsStatus: this.referenceDataStore.optionsStatus(),
    checklistsStatus: this.referenceDataStore.checklistsStatus(),
    materialsStatus: this.referenceDataStore.materialsStatus(),
  }));

  private readonly resourceStatuses = computed(() => Object.values(this.referenceDataStatus()));
  private readonly rawError = computed(() => this.sessionStore.error() ?? this.referenceDataStore.error());
  private readonly isUnauthenticatedReleased = computed(() =>
    !this.sessionStore.isAuthenticated() &&
    (this.authenticationStatus() === 'idle' || this.authenticationStatus() === 'loaded'));

  private readonly bootstrapStarted = computed(() =>
    this.bootstrapStatus() !== 'idle' || this.resourceStatuses().some((status) => status !== 'idle'));

  readonly isAnyResourceLoading = computed(() => this.resourceStatuses().some((status) => status === 'loading'));
  readonly allReferenceDataLoaded = computed(() => this.resourceStatuses().every((status) => status === 'loaded'));

  readonly isBootstrapErrored = computed(() =>
    this.bootstrapStatus() === 'error' ||
    this.resourceStatuses().some((status) => status === 'error') ||
    this.rawError() !== null);

  readonly isBootstrapLoading = computed(() =>
    !this.isBootstrapErrored() &&
    this.bootstrapStarted() &&
    (this.bootstrapStatus() === 'loading' || !this.allReferenceDataLoaded()));

  readonly showOverlay = computed(() =>
    !this.isPendingSiteActivation()
    && !this.isUnauthenticatedReleased()
    && (this.isBootstrapLoading() || this.isBootstrapErrored()));

  readonly errorMessageKey = computed(() => {
    const error = (this.rawError() ?? '').toLowerCase();

    if (/auth|session|sign[ -]?in|unauthori[sz]ed/.test(error)) {
      return BOOTSTRAP_ERROR_MESSAGE_KEYS.authentication;
    }
    if (/network|offline|timeout|timed out|connection|fetch/.test(error)) {
      return BOOTSTRAP_ERROR_MESSAGE_KEYS.network;
    }
    if (/permission|denied|forbidden/.test(error)) {
      return BOOTSTRAP_ERROR_MESSAGE_KEYS.permission;
    }
    if (/not found|missing|404/.test(error)) {
      return BOOTSTRAP_ERROR_MESSAGE_KEYS.missing;
    }
    return BOOTSTRAP_ERROR_MESSAGE_KEYS.unknown;
  });

  constructor() {
    effect(() => {
      const error = this.rawError();
      if (this.isBootstrapErrored()) {
        console.error('Application bootstrap failed.', error);
      }
    });
  }

  retry() {
    this.initializeService.retry();
  }
}
